import React, {
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { get } from 'lodash';
import { Alert } from 'react-bootstrap';
import styled from '@emotion/styled';

import colors from 'resources/styles/colors';
import SVButton from 'components/elements/SVButton';
import { LoaderIcon } from 'components/elements/Loader';

import {
  getReviwerComments,
  addReviewerComment,
  updateReviewerComment,
} from 'store/actions/comments';

const CommentContainer = styled.div`
  border-bottom: 1px solid ${colors.gray};
  padding: 10px 5%;
  min-height: 110px;
`;

const CommentInput = styled.textarea`
  resize: vertical;
  border-radius: 5px;
  border: 1px solid ${colors.gray};
  width: 100%;
`;

interface SiteReviewerCommentsProps {
  applicationId: number;
  surveyVersionId: number;
  criteriaId: string;
  readOnly?: boolean;
}

const SiteReviewerComments: React.FC<SiteReviewerCommentsProps> = ({
  applicationId,
  surveyVersionId,
  criteriaId,
  readOnly,
}) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, AnyAction>>();
  const textInput = useRef<HTMLTextAreaElement | null>(null);

  const reviewerComments = useSelector(
    (state: any) => state.comments.reviewerComments,
  );

  const commentTxt = useMemo(() => get(reviewerComments?.data, 'items[0].message', ''), [reviewerComments]);

  const data = useMemo(() => reviewerComments?.data, [reviewerComments]);

  useEffect(() => {
    dispatch(
      getReviwerComments({
        applicationId,
        surveyVersionId,
        criteriaId,
      }),
    );
  }, [dispatch, applicationId, criteriaId, surveyVersionId]);

  const handleCommentAdd = useCallback(
    (message: string) => {
      dispatch(
        addReviewerComment({
          applicationId,
          surveyVersionId,
          criteriaId,
          message,
        }),
      );
    },
    [applicationId, criteriaId, dispatch, surveyVersionId],
  );

  const handleCommentUpdate = useCallback(
    (commentId: number, message: string) => {
      dispatch(
        updateReviewerComment({
          commentId,
          message,
        }),
      );
    },
    [dispatch],
  );

  const handleCommentClick = useCallback(() => {
    if (!data?.items) return;
    const message = textInput.current?.value ?? '';
    if (!message.trim()) return;

    if (data.items[0]) {
      handleCommentUpdate(data.items[0].id, message);
    } else {
      handleCommentAdd(message);
    }
  }, [data, handleCommentAdd, handleCommentUpdate]);

  const renderContent = useMemo(() => {
    if (reviewerComments?.isLoading) return <LoaderIcon />;
    if (reviewerComments?.error) {
      return <Alert variant="danger">{reviewerComments.error}</Alert>;
    }

    return (
      <>
        <h6>Comment for Final Site Report</h6>
        <CommentInput
          defaultValue={commentTxt}
          ref={textInput}
          disabled={readOnly}
        />
        {!readOnly && (
          <SVButton variant="secondary" size="sm" onClick={handleCommentClick}>
            {`${commentTxt ? 'Update' : 'Add'} comment`}
          </SVButton>
        )}
      </>
    );
  }, [reviewerComments, commentTxt, handleCommentClick, readOnly]);

  return <CommentContainer>{renderContent}</CommentContainer>;
};

export default SiteReviewerComments;
